/* One native modal keeps navigation/focus stable while its two retained views
 * crossfade. Only Shopify handles form submissions; this file sends no data. */
(() => {
  if (window.AuluoServices) return;
  window.AuluoServices = true;
  const ids = ['contacto', 'privacidad'];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const animations = new Set();
  let dialog, views = [], nav, indicator, indicatorMotion;
  let desired = null, revision = 0, closePending = false;
  let returnFocus = null, returnScroll = 0;
  let fromUrl = location.pathname + location.search;
  const positions = new Map();

  function selected() { return ids.includes(location.hash.slice(1)) ? location.hash.slice(1) : null; }
  function cancelAnimations() {
    for (const animation of animations) animation.cancel();
    animations.clear();
  }
  function snapshot(element) {
    const style = getComputedStyle(element);
    return {opacity: style.opacity, transform: style.transform};
  }
  function animate(element, frames, duration) {
    if (reduced.matches || typeof element.animate !== 'function') return Promise.resolve();
    const animation = element.animate(frames, {duration, easing: ease, fill: 'both'});
    animations.add(animation);
    return animation.finished.catch(() => {});
  }
  function markNavigation(id, animateFrom) {
    if (!nav) return;
    nav.querySelectorAll('a').forEach(link => {
      if (link.hash === '#' + id) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    const link = nav.querySelector('a[href="#' + id + '"]');
    if (!link || !indicator) return;
    const box = nav.getBoundingClientRect(), rect = link.getBoundingClientRect();
    const end = {transform: 'translateX(' + (rect.left - box.left) + 'px)', width: rect.width + 'px'};
    indicatorMotion?.cancel();
    indicator.style.transform = end.transform;
    indicator.style.width = end.width;
    if (animateFrom && !reduced.matches && typeof indicator.animate === 'function') {
      indicatorMotion = indicator.animate([animateFrom, end], {duration: 420, easing: ease});
    }
  }
  function finish(id) {
    cancelAnimations();
    dialog.removeAttribute('data-a48-phase');
    if (id) {
      views.forEach(view => { view.hidden = view.id !== id; view.inert = view.id !== id; });
      return;
    }
    indicatorMotion?.cancel();
    indicatorMotion = null;
    dialog.close();
    document.documentElement.classList.remove('a47-panel-open');
    views.forEach(view => { view.hidden = true; view.inert = true; });
    positions.clear();
    closePending = false;
    window.scrollTo({top: returnScroll, behavior: 'instant'});
    if (returnFocus?.isConnected) returnFocus.focus({preventScroll: true});
  }
  function focusView(view) {
    const heading = view.querySelector('[data-a47-contact-status], h1');
    if (heading) { heading.tabIndex = -1; heading.focus({preventScroll: true}); }
  }
  function transition(id, trigger) {
    if (!dialog || (id === desired && (dialog.open || !id))) return;
    const wasOpen = dialog.open;
    const previous = desired;
    const token = ++revision;
    const shellStart = wasOpen ? snapshot(dialog) : {opacity: '0', transform: 'translateY(24px)'};
    const viewStarts = new Map(views.filter(view => !view.hidden).map(view => [view, snapshot(view)]));
    const navStart = indicator && wasOpen ? {transform: getComputedStyle(indicator).transform, width: getComputedStyle(indicator).width} : null;
    if (previous) {
      const previousView = views.find(view => view.id === previous);
      if (previousView) positions.set(previous, previousView.scrollTop);
    }
    desired = id;
    cancelAnimations();
    if (!id) {
      if (!wasOpen) return;
      dialog.dataset.a48Phase = 'closing';
      // Freeze an interrupted crossfade during the exit, instead of flashing.
      for (const [view, frame] of viewStarts) {
        view.inert = true;
        animate(view, [frame, frame], 300);
      }
      animate(dialog, [shellStart, {opacity: '0', transform: 'translateY(20px)'}], 300).then(() => {
        if (token === revision) finish(null);
      });
      return;
    }
    closePending = false;
    const target = views.find(view => view.id === id);
    if (!target) return;
    const direction = ids.indexOf(id) >= ids.indexOf(previous) ? 1 : -1;
    if (!wasOpen) {
      returnFocus = trigger || document.activeElement;
      returnScroll = window.scrollY;
      views.forEach(view => { view.hidden = view !== target; view.inert = view !== target; });
      target.scrollTop = 0;
      document.documentElement.classList.add('a47-panel-open');
      dialog.showModal();
    }
    target.hidden = false;
    target.inert = false;
    target.scrollTop = positions.get(id) || 0;
    dialog.setAttribute('aria-labelledby', target.getAttribute('aria-labelledby'));
    dialog.dataset.a48Phase = wasOpen ? 'switching' : 'opening';
    markNavigation(id, wasOpen ? navStart : null);
    focusView(target);
    const tasks = [];
    // The shell only moves on entry/exit or finishes an interrupted entry.
    if (!wasOpen || shellStart.opacity !== '1' || shellStart.transform !== 'none') {
      tasks.push(animate(dialog, [shellStart, {opacity: '1', transform: 'none'}], wasOpen ? 360 : 500));
    }
    const incoming = (wasOpen && viewStarts.get(target)) || {opacity: '0', transform: wasOpen ? 'translateX(' + (direction * 20) + 'px)' : 'translateY(16px)'};
    tasks.push(animate(target, [incoming, {opacity: '1', transform: 'none'}], wasOpen ? 420 : 540));
    for (const view of views) {
      if (view === target || !viewStarts.has(view) || !wasOpen) continue;
      view.inert = true;
      tasks.push(animate(view, [viewStarts.get(view), {opacity: '0', transform: 'translateX(' + (-direction * 14) + 'px)'}], 320));
    }
    Promise.all(tasks).then(() => { if (token === revision) finish(id); });
  }
  function sync() { transition(selected()); }
  function close() {
    if (!dialog?.open || closePending) return;
    closePending = true;
    if (history.state?.a48Panel) history.back();
    else {
      history.replaceState(history.state, '', fromUrl);
      transition(null);
    }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a');
    if (!link || (link.target && link.target !== '_self')) return;
    if (link.hasAttribute('data-a47-close') && dialog?.open) { event.preventDefault(); close(); return; }
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
    const id = url.hash.slice(1);
    if (!ids.includes(id) || !dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    if (closePending) return;
    if (!dialog.open) {
      fromUrl = location.pathname + location.search + location.hash;
      history.pushState({...history.state, a48Panel: true}, '', url.hash);
    } else history.replaceState(history.state, '', url.hash);
    transition(id, link);
  });
  document.addEventListener('cancel', event => {
    if (event.target === dialog) { event.preventDefault(); close(); }
  }, true);
  window.addEventListener('popstate', sync);
  window.addEventListener('hashchange', sync);
  window.addEventListener('resize', () => { if (dialog?.open && desired) markNavigation(desired); }, {passive: true});
  reduced.addEventListener('change', () => {
    if (!reduced.matches || !dialog?.open) return;
    ++revision;
    indicatorMotion?.cancel();
    finish(desired);
  });
  function init() {
    const element = document.getElementById('auluo-services');
    if (!element || typeof element.showModal !== 'function') return;
    if (element !== dialog) {
      dialog = element;
      views = Array.from(dialog.querySelectorAll('.a48-view'));
      nav = dialog.querySelector('.a47-service-nav');
      indicator = document.createElement('span');
      indicator.className = 'a48-nav-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      nav.appendChild(indicator);
      dialog.dataset.a48Enhanced = '';
      desired = null;
    }
    const status = document.querySelector('#contacto [data-a47-contact-status]');
    if (status && !selected()) history.replaceState(history.state, '', '#contacto');
    sync();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    if (dialog && event.target.contains(dialog)) {
      ++revision;
      if (dialog.open) finish(null);
      else cancelAnimations();
      desired = null; dialog = null;
    }
  });
  document.addEventListener('shopify:section:select', event => {
    if (event.target.querySelector('[data-a47-services]')) transition('contacto');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
